import { FC, ReactNode } from 'react';
import { TruncateTooltip } from 'ui/utility/truncate/TruncateTooltip';
import { TipPlacement } from 'ui/tooltip';
import { MenuItemText, MenuItemContentWrapper, StyledCheckbox } from './Styled';

type CheckboxMenuProps = {
  /** Component that will be displayed in the menu item if label or children is not provided. */
  content?: ReactNode;
  /**  Identifier of the menu item */
  id: string;
  /** If true, the menu item checkbox will be selected */
  isSelected?: boolean;
  /** Text that will be displayed in the menu item if content or children is not provided.
  Required if the menu has search filter, as the string representation of the content displayed */
  label?: string;
  /** Callback function to call when there is a selection change */
  onSelectionChange?: (id: string) => void;
  /** Whether or not the item is selectable */
  isDisabled?: boolean;
};

export const CheckboxMenuItem: FC<React.PropsWithChildren<CheckboxMenuProps>> = ({
  children,
  content,
  id,
  isSelected,
  label,
  isDisabled,
  onSelectionChange = () => null,
}) => {
  // Function to handle checkbox status change
  const handleCheckboxChange = () => {
    if (!isDisabled) {
      onSelectionChange(id);
    }
  };

  // Determine the content to display, using TruncateTooltip for the label if present
  const displayContent =
    children ||
    content ||
    (label && (
      <TruncateTooltip
        name={label}
        tooltipProps={{ placement: TipPlacement.Right, delayDuration: 300 }}
        textOnly
        maxWidth={176}
      />
    ));
  return (
    <MenuItemContentWrapper as="li" data-test={`checkbox-menu-item-${id}`} isDisabled={isDisabled}>
      <StyledCheckbox
        id={`checkbox-${id}`}
        setChecked={handleCheckboxChange}
        status={!!isSelected}
        isDisabled={isDisabled}
        label={<MenuItemText>{displayContent}</MenuItemText>}
      />
    </MenuItemContentWrapper>
  );
};
